import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Badge,
  Card,
  Modal,
  ProgressBar,
  Form,
  Alert,
} from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaStar,
  FaPlay,
  FaTimes,
  FaShare,
  FaComment,
  FaChevronRight,
  FaHome,
  FaSearch,
  FaCompass,
  FaPaperPlane,
  FaUser,
  FaArrowLeft,
  FaFilm,
  FaUsers,
  FaClock,
  FaGlobe,
} from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { moviesAPI } from "../services/api";
import api from "../services/api";
import ModernLoader from "../components/common/ModernLoader";
import { useAuth } from "@clerk/clerk-react";
import "../styles/movie-details-cast.css";

import { getMoviePosterUrl, getBackdropUrl } from "../utils/imageUtils";

const MovieDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, user, getToken } = useAuth();
  const reviewsRef = useRef(null);
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 0, comment: "" });
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [failedCastImages, setFailedCastImages] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMovie();
  }, [id]);

  const fetchMovie = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await moviesAPI.getById(id);
      const movieData = response.data;

      if (!movieData) {
        console.error("Movie not found");
        navigate("/movies");
        return;
      }

      console.log(
        "Fetched movie:",
        movieData.title,
        "Trailer:",
        movieData.youtubeUrl || movieData.trailer
      );
      setMovie(movieData);
      setReviews(movieData.reviews || []);
      setAverageRating(movieData.averageRating || 0);
      setTotalRatings(movieData.totalRatings || 0);

      // Use youtubeUrl if available, otherwise try to fetch from TMDB
      if (!movieData.youtubeUrl && !movieData.trailer && movieData.externalId) {
        fetchTrailerFromTMDB(movieData.externalId);
      }
    } catch (error) {
      console.error("Error fetching movie:", error);
      navigate("/movies");
    } finally {
      setLoading(false);
    }
  };

  const fetchTrailerFromTMDB = async (tmdbId) => {
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/movie/${tmdbId}/videos?api_key=${process.env.REACT_APP_TMDB_API_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const trailer = data.results.find(
          (v) => v.type === "Trailer" && v.site === "YouTube"
        );
        if (trailer) {
          setMovie((prev) => ({
            ...prev,
            trailer: `https://www.youtube.com/embed/${trailer.key}`,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching trailer from TMDB:", error);
    }
  };

  // Function to handle booking tickets with pre-selected city
  const handleBookTickets = () => {
    const savedLocation = localStorage.getItem("userLocation");
    let city = "";

    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        city = location.city;
      } catch (err) {
        console.error("Error parsing saved location:", err);
      }
    }

    if (city) {
      navigate(
        `/movie/${movie._id}/showtimes?city=${encodeURIComponent(city)}`
      );
    } else {
      navigate(`/movie/${movie._id}/showtimes`);
    }
  };

  const fetchRatings = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await api.get(`/ratings/movie/${id}`, {
        params: { _t: timestamp },
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      setReviews(response.data.ratings || []);
      setAverageRating(parseFloat(response.data.averageRating) || 0);
      setTotalRatings(response.data.totalRatings || 0);
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setReviews([]);
      setAverageRating(0);
      setTotalRatings(0);
    }
  };

  const fetchUserRating = async () => {
    try {
      const response = await api.get(`/ratings/movie/${id}/user`, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
        },
      });
      if (response.data && response.data.rating) {
        setUserRating(response.data.rating);
      }
    } catch (error) {
      console.error("Error fetching user rating:", error);
    }
  };

  const submitReview = async () => {
    if (!isSignedIn) {
      setShowAlert({
        show: true,
        message: "Please sign in to submit a review",
        type: "warning",
      });
      setTimeout(() => setShowAlert({ show: false }), 3000);
      return;
    }

    if (newReview.rating === 0 || !newReview.comment.trim()) {
      setShowAlert({
        show: true,
        message: "Please provide both rating and comment",
        type: "warning",
      });
      setTimeout(() => setShowAlert({ show: false }), 3000);
      return;
    }

    try {
      await api.post(
        `/ratings/movie/${id}`,
        {
          rating: newReview.rating,
          comment: newReview.comment.trim(),
        },
        {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
          },
        }
      );

      setNewReview({ rating: 0, comment: "" });
      setUserRating(0);
      fetchMovie();
      setShowAlert({
        show: true,
        message: "Review submitted successfully!",
        type: "success",
      });
      setTimeout(() => setShowAlert({ show: false }), 3000);
    } catch (error) {
      setShowAlert({
        show: true,
        message: "Failed to submit review",
        type: "danger",
      });
      setTimeout(() => setShowAlert({ show: false }), 3000);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes("embed")) return url;
    if (url.includes("youtube.com/watch?v=")) {
      const videoId = url.split("v=")[1]?.split("&")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (url.includes("youtu.be/")) {
      const videoId = url.split("youtu.be/")[1]?.split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    return null;
  };

  const hasValidTrailer = () => {
    const trailerUrl = movie?.youtubeUrl || movie?.trailer;
    return trailerUrl && trailerUrl !== "null" && trailerUrl.trim() !== "";
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return "";
    const textarea = document.createElement("textarea");
    textarea.textContent = text;
    return textarea.value;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: movie.title,
          text: movie.description,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Share failed:", error);
      }
    }
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const calculateRatingBreakdown = () => {
    if (reviews.length === 0) return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      breakdown[review.rating] = (breakdown[review.rating] || 0) + 1;
    });

    Object.keys(breakdown).forEach((key) => {
      breakdown[key] = Math.round((breakdown[key] / reviews.length) * 100);
    });

    return breakdown;
  };

  if (loading) return <ModernLoader text="Loading Movie Details" />;
  if (!movie)
    return <div className="text-center text-white mt-5">Movie not found</div>;

  const ratingBreakdown = calculateRatingBreakdown();

  return (
    <div
      style={{
        backgroundColor: "#1f2025",
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      {/* Desktop Hero Section - 100vh Backdrop */}
      <section
        className="d-none d-md-block"
        style={{
          backgroundImage: `linear-gradient(90deg, rgb(26, 26, 26) 24.97%, rgb(26, 26, 26) 38.3%, rgba(26, 26, 26, 0.04) 97.47%, rgb(26, 26, 26) 100%), url(${
            getBackdropUrl(movie.backdrop, movie.poster, movie.title) ||
            `https://via.placeholder.com/1920x1080/1e293b/ffffff?text=${encodeURIComponent(
              movie.title
            )}`
          })`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          paddingLeft: "calc(50vw - 50%)",
          paddingRight: "calc(50vw - 50%)",
          paddingTop: "100px",
          paddingBottom: "40px",
          boxSizing: "border-box",
        }}
      >
        <Container
          className="w-100"
          style={{ position: "relative", zIndex: 2 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "3rem",
              paddingTop: "100px",
            }}
          >
            {/* Poster */}
            <div style={{ flex: "0 0 auto" }}>
              <div
                className="position-relative overflow-hidden d-flex justify-content-center"
                style={{ borderRadius: "16px" }}
              >
                <img
                  src={getMoviePosterUrl(movie.poster, movie.title)}
                  alt={movie.title}
                  style={{
                    width: "280px",
                    height: "416px",
                    objectFit: "cover",
                    borderRadius: "16px",
                  }}
                  onError={(e) => {
                    e.target.src = "https://picsum.photos/280/416";
                  }}
                />
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1 }}>
              {/* Movie Title */}
              <h1
                className="text-white fw-bold mb-4"
                style={{ fontSize: "3rem", lineHeight: "1.2" }}
              >
                {movie.title}
              </h1>

              {/* Rating Section */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={star}
                          className={
                            star <= Math.round(averageRating)
                              ? "text-warning"
                              : "text-secondary"
                          }
                          size={20}
                        />
                      ))}
                    </div>
                    <h5 className="text-white mb-0 fw-semibold ms-2">
                      {averageRating.toFixed(1)}/5
                    </h5>
                    <h6 className="text-light mb-0">({totalRatings})</h6>
                  </div>
                </div>
                <Button
                  variant="outline-light"
                  size="sm"
                  className="px-3 py-1"
                  style={{ fontSize: "14px" }}
                  onClick={scrollToReviews}
                >
                  Rate now
                </Button>
              </div>

              {/* Format Tags */}
              <div className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge
                    className="px-2 py-1 text-decoration-none"
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #666",
                      color: "#fff",
                      fontSize: "12px",
                    }}
                  >
                    2D
                  </Badge>
                  <Badge
                    className="px-2 py-1 text-decoration-none"
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #666",
                      color: "#fff",
                      fontSize: "12px",
                    }}
                  >
                    Hindi
                  </Badge>
                </div>
              </div>

              {/* Movie Meta */}
              <div
                className="d-flex align-items-center gap-2 mb-4 text-light flex-wrap"
                style={{ fontSize: "14px" }}
              >
                <span>{movie.duration || movie.runtime || "2h 32m"}</span>
                <span>•</span>
                <span className="text-danger text-decoration-none">
                  {Array.isArray(movie.genre)
                    ? movie.genre.join(", ")
                    : movie.genre || "Action, Crime"}
                </span>
                <span>•</span>
                <span
                  className="px-2 py-1 rounded"
                  style={{
                    backgroundColor: "rgba(255, 193, 7, 0.2)",
                    color: "#ffc107",
                    fontWeight: "bold",
                  }}
                >
                  {movie.filmGrade || movie.certification || "U/A"}
                </span>
                <span>•</span>
                <span>
                  {new Date(movie.releaseDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }) || "11 Jul, 2025"}
                </span>
              </div>

              {/* Director */}
              {movie.director && (
                <div className="mb-3">
                  <p className="text-light mb-1" style={{ fontSize: "13px" }}>
                    <span className="text-muted">Director:</span>{" "}
                    {movie.director}
                  </p>
                </div>
              )}

              {/* Cast */}
              {movie.cast && movie.cast.length > 0 && (
                <div className="mb-4">
                  <h6
                    className="text-muted mb-2"
                    style={{
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Cast
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {movie.cast.slice(0, 5).map((actor, idx) => (
                      <span
                        key={idx}
                        className="text-light"
                        style={{ fontSize: "13px" }}
                      >
                        {actor.name}
                        {idx < Math.min(4, movie.cast.length - 1) ? ", " : ""}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Book Tickets Button */}
              <div className="d-flex gap-3 mb-4">
                <Button
                  variant="danger"
                  className="px-5 py-3 fw-semibold"
                  style={{
                    fontSize: "16px",
                    borderRadius: "8px",
                    fontWeight: "500",
                  }}
                  onClick={handleBookTickets}
                >
                  Book tickets
                </Button>
                {hasValidTrailer() && (
                  <button
                    type="button"
                    className="d-flex align-items-center justify-content-center px-5 py-3 fw-semibold btn btn-outline-light"
                    style={{
                      fontSize: "16px",
                      borderRadius: "8px",
                      fontWeight: "500",
                      gap: "8px",
                    }}
                    onClick={() => setShowTrailerModal(true)}
                  >
                    <FaPlay size={14} />
                    Watch Trailer
                  </button>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>
      {/* About Movie Section - Desktop Only */}
      {movie.description && (
        <section
          className="d-none d-md-block"
          style={{
            backgroundColor: "#1f2025",
            paddingTop: "2rem",
            paddingBottom: "2rem",
            paddingLeft: "1rem",
            paddingRight: "1rem",
          }}
        >
          <Container>
            <div style={{ maxWidth: "100%" }}>
              <h5 className="text-white mb-3" style={{ fontSize: "1.1rem" }}>About the Movie</h5>
              <p
                className="text-light mb-0"
                style={{ fontSize: "clamp(13px, 2vw, 14px)", lineHeight: "1.6" }}
              >
                {movie.description}
              </p>
            </div>
          </Container>
        </section>
      )}

      {/* Cast Section with Round Images - Desktop Only */}
      {movie.cast && movie.cast.length > 0 && (
        <section className="cast-section d-none d-md-block">
          <Container>
            <div className="section-header">
              <FaUsers className="section-header-icon" />
              <h3>Cast</h3>
            </div>
            <div className="cast-container">
              {movie.cast.map((actor, index) => (
                <motion.div
                  key={index}
                  className="cast-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <div className="cast-image-wrapper">
                    {actor.profilePath && !failedCastImages.has(index) ? (
                      <img
                        src={actor.profilePath}
                        alt={actor.name}
                        className="cast-image"
                        onError={() => {
                          setFailedCastImages(
                            (prev) => new Set([...prev, index])
                          );
                        }}
                      />
                    ) : null}
                    {!actor.profilePath || failedCastImages.has(index) ? (
                      <div className="cast-placeholder">
                        {actor.name.charAt(0).toUpperCase()}
                      </div>
                    ) : null}
                  </div>
                  <div className="cast-name" title={actor.name}>
                    {actor.name}
                  </div>
                  {actor.character && (
                    <div className="cast-character" title={actor.character}>
                      as {actor.character}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      )}


      {/* Mobile Movie Info Section */}
      <div
        className="d-md-none"
        style={{ paddingTop: "80px" }}
      >
        {/* Mobile Trailer Banner - Full Width */}
        <section className="position-relative mx-3 mb-3">
          <div className="position-relative" style={{ width: "100%" }}>
            <div
              style={{
                height: "240px",
                width: "100%",
                background: "none",
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <img
                src={getMoviePosterUrl(movie.poster, movie.title)}
                alt="multimedia-trailers"
                style={{
                  opacity: 1,
                  objectFit: "cover",
                  width: "100%",
                  height: "240px",
                }}
                onError={(e) => {
                  e.target.src = "https://picsum.photos/300/400";
                }}
              />
            </div>

            {/* Trailer and Book Ticket Buttons - Improved Mobile UI */}
            <div
              className="position-absolute d-flex gap-2 w-100"
              style={{
                bottom: "8px",
                left: "8px",
                right: "8px",
                padding: "0 4px",
              }}
            >
              {hasValidTrailer() && (
                <button
                  type="button"
                  className="d-flex align-items-center justify-content-center text-white px-2 py-2 border-0"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.85)",
                    borderRadius: "8px",
                    fontSize: "10px",
                    fontWeight: "600",
                    backdropFilter: "blur(15px)",
                    minWidth: "60px",
                    height: "32px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    gap: "4px",
                  }}
                  onClick={() => setShowTrailerModal(true)}
                >
                  <FaPlay size={8} />
                  <span>Trailer</span>
                </button>
              )}
              <button
                type="button"
                className="px-3 py-2 fw-bold btn btn-danger flex-grow-1 border-0"
                style={{
                  fontSize: "12px",
                  borderRadius: "8px",
                  fontWeight: "700",
                  height: "32px",
                  background:
                    "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                  boxShadow: "0 2px 8px rgba(220, 53, 69, 0.4)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  margin: "0 20px",
                }}
                onClick={handleBookTickets}
              >
                Book Now
              </button>
            </div>

            {/* In Cinemas Badge */}
            <div
              className="position-absolute d-flex align-items-center px-3 py-2 text-white"
              style={{
                top: "12px",
                right: "12px",
                backgroundColor: "rgba(229, 9, 20, 0.9)",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              <span>In Cinemas</span>
            </div>
          </div>
        </section>

        {/* Mobile Movie Info */}
        <section className="px-3 py-4">
          <div className="mb-4">
            <h1
              className="text-white fw-bold mb-4"
              style={{ fontSize: "1.5rem", lineHeight: "1.3" }}
            >
              {movie.title}
            </h1>

            {/* Mobile Rating */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="d-flex align-items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={
                        star <= Math.round(averageRating)
                          ? "text-warning"
                          : "text-secondary"
                      }
                      size={14}
                    />
                  ))}
                </div>
                <span
                  className="text-white fw-bold"
                  style={{ fontSize: "14px" }}
                >
                  {averageRating.toFixed(1)}/5
                </span>
                <small className="text-light">({totalRatings} votes)</small>
              </div>
            </div>

            {/* Mobile Format Tags */}
            <div className="d-flex gap-2 mb-4 flex-wrap">
              <span
                className="px-3 py-2 text-white fw-semibold"
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: "11px",
                  borderRadius: "6px",
                }}
              >
                2D
              </span>
              <span
                className="px-3 py-2 text-white fw-semibold"
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: "11px",
                  borderRadius: "6px",
                }}
              >
                HINDI
              </span>
            </div>

            {/* Mobile Meta */}
            <div className="mb-4">
              <div
                className="text-light"
                style={{ fontSize: "13px", lineHeight: "1.8" }}
              >
                <div className="mb-2">
                  <span className="me-2">
                    {movie.duration || movie.runtime || "2h 32m"}
                  </span>
                  <span className="me-2">•</span>
                  <span>
                    {Array.isArray(movie.genre)
                      ? movie.genre.join(", ")
                      : "Action"}
                  </span>
                </div>
                <div>
                  <span
                    className="px-2 py-1 rounded"
                    style={{
                      backgroundColor: "rgba(255, 193, 7, 0.2)",
                      color: "#ffc107",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  >
                    {movie.filmGrade || movie.certification || "U/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Director */}
            {movie.director && (
              <div className="mb-3">
                <p
                  className="text-light mb-0"
                  style={{ fontSize: "13px", lineHeight: "1.6" }}
                >
                  <span className="text-muted d-block mb-1">Director</span>
                  <span>{movie.director}</span>
                </p>
              </div>
            )}


          </div>
        </section>

        {/* Mobile Movie Description */}
        <motion.section
          className="px-3 pb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          data-aos="fade-up"
          data-aos-duration="600"
        >
          <motion.div
            className="p-3 rounded"
            style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            data-aos="zoom-in"
            data-aos-delay="150"
          >
            <h6
              className="text-white mb-2 fw-semibold"
              data-aos="fade-right"
              data-aos-delay="300"
            >
              About the Movie
            </h6>
            <p
              className="text-light mb-0"
              style={{ fontSize: "14px", lineHeight: "1.6" }}
              data-aos="fade-up"
              data-aos-delay="450"
            >
              {movie.description ||
                "Power. Loyalty. Betrayal. One man's rise to rule it all. A gripping gangster action thriller that will keep you on the edge of your seat."}
            </p>
          </motion.div>
        </motion.section>

        {/* Mobile Cast Section */}
        {movie.cast && movie.cast.length > 0 && (
          <motion.section
            className="px-3 pb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="100"
          >
            <div className="mb-3">
              <h6
                className="text-white mb-3 fw-semibold d-flex align-items-center gap-2"
                style={{ fontSize: "14px" }}
              >
                <FaUsers size={16} className="text-danger" />
                Cast
              </h6>
              <div
                className="d-flex gap-2 overflow-x-auto pb-2"
                style={{ scrollBehavior: "smooth", marginLeft: "-12px", marginRight: "-12px", paddingLeft: "12px", paddingRight: "12px" }}
              >
                {movie.cast.slice(0, 6).map((actor, idx) => (
                  <motion.div
                    key={idx}
                    className="flex-shrink-0"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    style={{ minWidth: "85px" }}
                  >
                    <div
                      className="text-center"
                      style={{
                        borderRadius: "50%",
                      }}
                    >
                      <div
                        className="rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{
                          width: "70px",
                          height: "70px",
                          backgroundColor: "rgba(220, 53, 69, 0.3)",
                          fontSize: "20px",
                          overflow: "hidden",
                          borderRadius: "50%",
                        }}
                      >
                        {actor.profilePath && !failedCastImages.has(idx) ? (
                          <img
                            src={actor.profilePath}
                            alt={actor.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={() => {
                              setFailedCastImages(
                                (prev) => new Set([...prev, idx])
                              );
                            }}
                          />
                        ) : (
                          actor.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <p
                        className="text-white mb-1"
                        style={{
                          fontSize: "10px",
                          fontWeight: "600",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: "1.2",
                        }}
                        title={actor.name}
                      >
                        {actor.name}
                      </p>
                      {actor.character && (
                        <p
                          className="text-muted mb-0"
                          style={{
                            fontSize: "8px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: "1",
                          }}
                          title={actor.character}
                        >
                          as {actor.character}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}
      </div>

      <Container className="py-3" ref={reviewsRef}>
        <div
          className="text-center mb-3"
          data-aos="fade-down"
          data-aos-duration="600"
        >
          <FaComment
            className="text-danger mb-2"
            size={24}
            data-aos="bounce"
            data-aos-delay="200"
          />
          <h4
            className="text-white fw-bold mb-1"
            data-aos="zoom-in"
            data-aos-delay="300"
          >
            User Reviews
          </h4>
          <p
            className="text-light mb-0"
            style={{ fontSize: "14px" }}
            data-aos="fade-up"
            data-aos-delay="400"
          >
            What our audience thinks about this movie
          </p>
        </div>

        <Row>
          <Col md={8}>
            {/* Add Review Form */}
            {isSignedIn && (
              <Card
                className="border-0 text-white shadow mb-3"
                style={{ backgroundColor: "#1f2025" }}
                data-aos="fade-up"
                data-aos-duration="500"
              >
                <Card.Body className="p-3">
                  <h6 className="text-white mb-2">Write a Review</h6>

                  <div className="d-flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FaStar
                        key={star}
                        className={
                          star <= newReview.rating
                            ? "text-warning"
                            : "text-muted"
                        }
                        onClick={() =>
                          setNewReview({ ...newReview, rating: star })
                        }
                        style={{ cursor: "pointer", fontSize: "1.2rem" }}
                      />
                    ))}
                  </div>

                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="border-secondary text-white mb-2"
                    style={{ backgroundColor: "#1f2025", fontSize: "13px" }}
                    placeholder="Share your thoughts..."
                    value={newReview.comment}
                    onChange={(e) =>
                      setNewReview({ ...newReview, comment: e.target.value })
                    }
                  />

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={submitReview}
                    disabled={
                      newReview.rating === 0 || newReview.comment.length < 5
                    }
                  >
                    Submit
                  </Button>
                </Card.Body>
              </Card>
            )}

            {showAlert.show && (
              <Alert variant={showAlert.type} className="mb-3">
                {showAlert.message}
              </Alert>
            )}

            {/* Reviews List */}
            {reviews && reviews.length > 0 ? (
              reviews
                .slice()
                .reverse()
                .slice(0, 5)
                .map((review, index) => {
                  if (!review) return null;
                  const displayName =
                    review.userName &&
                    review.userName.trim() &&
                    review.userName !== "undefined"
                      ? review.userName
                      : review.userEmail?.split("@")[0] || "User";
                  const initial = displayName.charAt(0).toUpperCase();
                  return (
                    <div
                      key={review._id || index}
                      className="border-bottom border-secondary pb-2 mb-3"
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                      data-aos-duration="500"
                    >
                      <div className="d-flex align-items-center mb-2">
                        <div
                          className="rounded-circle me-2 d-flex align-items-center justify-content-center text-white fw-bold"
                          style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "rgba(220, 53, 69, 0.6)",
                            fontSize: "14px",
                          }}
                        >
                          {initial}
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar
                                key={star}
                                className={
                                  star <= (review.rating || 0)
                                    ? "text-warning"
                                    : "text-muted"
                                }
                                size={12}
                              />
                            ))}
                            <span
                              className="text-white ms-1"
                              style={{ fontSize: "13px" }}
                            >
                              {displayName}
                            </span>
                          </div>
                          <small className="text-muted">
                            {review.createdAt
                              ? new Date(review.createdAt).toLocaleDateString()
                              : "Recently"}
                          </small>
                        </div>
                      </div>
                      <p
                        className="text-light mb-0"
                        style={{ fontSize: "13px" }}
                      >
                        {review.comment || "No comment provided"}
                      </p>
                    </div>
                  );
                })
            ) : (
              <div className="text-center text-light py-4">
                <p>No reviews yet. Be the first to review!</p>
              </div>
            )}
          </Col>

          <Col md={4}>
            <Card
              className="border-0 text-white shadow"
              style={{ backgroundColor: "#1f2025" }}
              data-aos="fade-right"
              data-aos-duration="600"
              data-aos-delay="200"
            >
              <Card.Body className="p-3">
                <h6 className="text-white mb-3">Rating Breakdown</h6>

                {[5, 4, 3, 2, 1].map((rating) => (
                  <div key={rating} className="d-flex align-items-center mb-2">
                    <span
                      className="text-white me-2"
                      style={{ fontSize: "12px" }}
                    >
                      {rating} ★
                    </span>
                    <ProgressBar
                      now={ratingBreakdown[rating] || 0}
                      className="flex-grow-1 me-2"
                      style={{ height: "6px" }}
                      variant="danger"
                    />
                    <span className="text-light" style={{ fontSize: "11px" }}>
                      {ratingBreakdown[rating] || 0}%
                    </span>
                  </div>
                ))}

                <div className="text-center mt-3 pt-2 border-top border-secondary">
                  <h5 className="text-warning mb-0">
                    {averageRating.toFixed(1)}/5
                  </h5>
                  <small className="text-light">
                    {totalRatings} {totalRatings === 1 ? "review" : "reviews"}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Trailer Modal */}
      <Modal
        show={showTrailerModal}
        onHide={() => setShowTrailerModal(false)}
        size="xl"
        centered
      >
        <Modal.Header className="bg-dark border-0 py-2 px-3 d-flex justify-content-between align-items-center">
          <Modal.Title
            className="text-white d-flex align-items-center mb-0"
            style={{ fontSize: "14px" }}
          >
            <FaPlay className="me-2" size={12} />
            <span className="d-none d-md-inline">
              {movie.title} - Official Trailer
            </span>
            <span className="d-md-none">
              {movie.title.length > 12
                ? movie.title.substring(0, 12) + "... Trailer"
                : movie.title + " Trailer"}
            </span>
          </Modal.Title>
          <button
            type="button"
            className="text-white p-0 btn btn-link"
            onClick={() => setShowTrailerModal(false)}
            style={{
              lineHeight: "1",
              margin: "0px",
              border: "none",
              outline: "none",
              width: "auto",
            }}
          >
            <FaTimes size={18} />
          </button>
        </Modal.Header>
        <Modal.Body className="bg-dark p-0">
          <div className="ratio ratio-16x9">
            <iframe
              src={getYouTubeEmbedUrl(movie.youtubeUrl || movie.trailer)}
              title={`${movie.title} Trailer`}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded"
              onLoad={() =>
                console.log(
                  "Trailer iframe loaded:",
                  movie.youtubeUrl || movie.trailer
                )
              }
            />
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MovieDetails;
